import type { AuditionTrack, InspectTrack } from "@/lib/music/analyzer-types";

/** GM programs in [0..7] are the piano family. */
const PIANO_GM_PROGRAM_MAX = 7;

/** Below this note count a track is too sparse to be a meaningful candidate. */
const MIN_CANDIDATE_NOTES = 16;

/**
 * A track is "probable" — pre-checked and grouped first in the manual
 * selector — when it's a strong candidate for the piano part:
 *   - the detected lead/sung melody (`isMelody`),
 *   - the bass line (`suggestedRole === "bass"`),
 *   - or a genuine piano-family track with enough material.
 *
 * Drum tracks never qualify. On a full arrangement this lands the user on the
 * "melody + bass" default rather than on the (untrusted) auto-detected hands.
 */
export function isProbableTrack(
  track: AuditionTrack,
  inspectTrack: InspectTrack | undefined,
): boolean {
  if (track.isDrumChannel) {
    return false;
  }

  if (inspectTrack?.isMelody || inspectTrack?.suggestedRole === "bass") {
    return true;
  }

  const isPianoFamily =
    track.gmProgram >= 0 && track.gmProgram <= PIANO_GM_PROGRAM_MAX;

  return isPianoFamily && track.notes.length >= MIN_CANDIDATE_NOTES;
}

/** Drum tracks can't be played as a piano part, so they're never selectable. */
export function isDisabledTrack(track: AuditionTrack): boolean {
  return track.isDrumChannel;
}
